import { pullReviews, sendError, mergeFilterSortReviews } from "backend/reviews-util";
import wixApplication from "wix-application";
import wixSeoFrontend from "wix-seo-frontend";
import wixLocation from "wix-location";

let totalItems,
    loopTime,
    loopInterval,
    loop,
    premium,
    hideBranding,
    hideLogo,
    hideDate,
    commentType,
    sort,
    filters,
    siteIdentifier,
    arrowColor,
    reviewTextSize,
    allReviews,
    currentContainerWidth,
    currentReviewsPerRow,
    lastReviewTextClass;
let reviews = [];
let currentIndex = 0;
let currentDisplayedReviews = [];

const defaultPfp =
    "https://static.wixstatic.com/shapes/ffd4d0_36e34b00c0fc4eaa97b841c6a9b3e099.svg";

const logos = {
    Facebook: "https://static.wixstatic.com/shapes/3b437f_3f571bd817bd4310ac33f31dbd921001.svg",
    Google: "https://static.wixstatic.com/shapes/3b437f_a0909c4b0c414364b2f8fde364beea52.svg",
};

$widget.onPropsChanged(async (oldProps, newProps) => {
  if (changed('loop', oldProps, newProps)) setLoopEnabled(newProps.loop);
  if (changed('hideBranding', oldProps, newProps)) toggleBranding(newProps.hideBranding);
  if (changed('hideLogo', oldProps, newProps)) toggleLogo(newProps.hideLogo);
  if (changed('hideDate', oldProps, newProps)) toggleDate(newProps.hideDate);
  if (changed('loopTime', oldProps, newProps)) setLoopTime(newProps.loopTime);
  if (changed('commentType', oldProps, newProps)) setCommentMode(newProps.commentType);
  if (changed('arrowColor', oldProps, newProps)) setArrowColor(newProps.arrowColor);
  if (changed('reviewTextSize', oldProps, newProps)) setReviewTextSize(newProps.reviewTextSize);

  // Single call if sort or filters changed
  if (changed('sort', oldProps, newProps) || changed('filters', oldProps, newProps)) {
    updateReviewsList(newProps.sort, newProps.filters);
  }
});

// Main entry point
$w.onReady(async function () {
    // Listen for width messages from the #sizeTrackerEmbed component
    $w('#sizeTracker').onMessage((event) => {
        if (event.data.type === 'widgetWidth') {
            currentContainerWidth = event.data.width;
            const newPerRow = calculateReviewsPerRow();
            if (newPerRow !== currentReviewsPerRow) {
                currentReviewsPerRow = newPerRow;
                updateReviewsForCurrentWidth(newPerRow, true); // Force refresh on resize
            }
        }
    });
    await initializeReviewsWidget();
});

// Initialize the reviews widget
async function initializeReviewsWidget(props = null) {
    try {
        const appInfo = await wixApplication.getDecodedAppInstance();
        siteIdentifier = appInfo.instanceId;
        premium = !!appInfo.vendorProductId;
        premium = premium ?? false;
        let serverSettings = await fetchReviewsData(siteIdentifier);
        allReviews = serverSettings.allReviews;
        props = props ? props : $widget.props;
        if (!$widget.props.migrated && premium) { // Server Side Settings Bind
            ({
                loop,
                logoHidden: hideLogo,
                dateHidden: hideDate,
                loopTime,
                reviewTextExpansion: commentType,
                sort,
                filters,
                reviews
            } = serverSettings);
            hideBranding = !serverSettings.branding;
            totalItems = reviews.length;
            if (totalItems === 0) return displayInactiveState();
        } else { // Client Side Props Settings Bind (Migrated)
            props = props ? props : $widget.props;
            ({
                loop,
                hideBranding,
                hideLogo,
                hideDate,
                loopTime,
                commentType,
                sort,
                filters,
            } = props);
            totalItems = allReviews.flat().length;
            if (totalItems === 0) return displayInactiveState();

            reviews = await mergeFilterSortReviews(allReviews, filters, sort, premium);
        }
        ({
            arrowColor,
            reviewTextSize,
        } = props);

        // Bind once
        $w('#reviewsRepeater').onItemReady(bindReviewItem);

        // Initial load – even if width not yet received, start with 1 column
        currentReviewsPerRow = calculateReviewsPerRow();
        currentIndex = 0; // Start from the beginning
        updateDisplayedReviews(); // Instead of loadAllReviews
        toggleNavigationButtons();
        setupNavigationButtons();
        setReviewTextSize();
        toggleBranding();
        toggleDate();
        toggleLogo();
        setArrowColor();
        setLoopEnabled();

        $w("#reviewsSlider").onMouseIn(() => {
            if (loop) stopLoop();
        });

        $w("#reviewsSlider").onMouseOut(() => {
            if (loop) startLoop();
        });
        //setReviewStructuredData(reviews);
    
        premium ? (hideBranding ? $w('#brandingBox').collapse() : $w('#brandingBox').expand()) : $w('#brandingBox').expand();
    } catch (error) {
        sendError(error?.stack, "Slider Binding", siteIdentifier);
    }
}

// Fetch and structure review data
async function fetchReviewsData(siteIdentifier) {
    const reviewData = await pullReviews(siteIdentifier);
    return {
        reviews: reviewData.reviews || [],
        allReviews: reviewData.allReviews || [],
        loop: reviewData.loop || false,
        branding: reviewData.branding,
        logoHidden: reviewData.logoHidden,
        dateHidden: reviewData.dateHidden,
        loopTime: reviewData.loopTime || 6,
        reviewTextExpansion: reviewData.reviewTextExpansion || "Scroll",
        sort: reviewData.sort || "Newest",
        filters: reviewData.filters
    };
}

// Display inactive state if no reviews are available
function displayInactiveState() {
    $w("#reviewsSlider").changeState("ReviewsInactive");
}

function updateReviewsForCurrentWidth(reviewsPerRow, forceRefresh = false) {
    const maxItems = reviewsPerRow;
    if (forceRefresh || maxItems !== currentDisplayedReviews.length) {
        currentReviewsPerRow = maxItems;
        updateDisplayedReviews(); // Recalculate the current view
        toggleNavigationButtons();
    }
}

function toggleNavigationButtons() {
    const shouldCollapse = reviews.length <= currentReviewsPerRow;

    if (shouldCollapse) {
        $w("#prevButton").collapse();
        $w("#nextButton").collapse();
    } else {
        $w("#prevButton").expand();
        $w("#nextButton").expand();
    }
}

function updateDisplayedReviews() {
    const itemsToShow = [];

    for (let i = 0; i < currentReviewsPerRow; i++) {
        const index = (currentIndex + i) % reviews.length;
        itemsToShow.push(reviews[index]);
    }

    currentDisplayedReviews = itemsToShow;
    $w('#reviewsRepeater').data = itemsToShow;
}

/**
 * Determine number of reviews per row based on #sizeTrackerEmbed width
 */
function calculateReviewsPerRow() {
    const width = currentContainerWidth || 1000;
    if (width <= 574) return 1;
    return 2 + Math.floor((width - 575) / 297);
}

// Review item binding
function bindReviewItem($item, $itemData) {
    $item("#name").text = $itemData.reviewerName || "Anonymous User";
    $item(`#date`).text = $itemData.reviewDate;
    $item(`#${commentType}Text`).text = $itemData.reviewText;
    $item(`#pfp`).src = $itemData.reviewerProfilePhoto || defaultPfp;
    $item(`#sourceLogo`).src = logos[$itemData.source || "Google"];
    $item(`#rateBox`).changeState(`${$itemData.rating}stars` || `5stars`);
    $item('#comment').changeState(commentType);
}

function setupNavigationButtons() {
    $w("#prevButton").onClick(() => {
        stopLoop();
        currentIndex = (currentIndex - currentReviewsPerRow + reviews.length) % reviews.length;
        updateDisplayedReviews();
        if (premium && loop) startLoop();
    });

    $w("#nextButton").onClick(() => {
        stopLoop();
        currentIndex = (currentIndex + currentReviewsPerRow) % reviews.length;
        updateDisplayedReviews();
        if (premium && loop) startLoop();
    });
}

function startLoop() {
  if (loopInterval) clearInterval(loopInterval);
  if (!loop || !reviews?.length || reviews.length <= currentReviewsPerRow) return;
  loopInterval = setInterval(() => {
    currentIndex = (currentIndex + currentReviewsPerRow) % reviews.length;
    updateDisplayedReviews();
  }, loopTime * 1000);
}
function stopLoop(){ if (loopInterval) clearInterval(loopInterval); loopInterval = null; }


function setLoopEnabled(newLoop = loop){
    loop = newLoop;
    loop ? startLoop() : stopLoop();
}

function toggleBranding(newHideBranding = hideBranding){
    hideBranding = newHideBranding;
    hideBranding ? $w('#brandingBox').collapse() : $w('#brandingBox').expand();
}

function toggleLogo(newHideLogo = hideLogo){
    hideLogo = newHideLogo;
    hideLogo ? $w('#sourceLogo').collapse() : $w('#sourceLogo').expand();
}

function toggleDate(newHideDate = hideDate){
    hideDate = newHideDate;
    hideDate ? $w('#date').collapse() : $w('#date').expand();
}

function setLoopTime(newLoopTime = loopTime) {
  loopTime = newLoopTime;
  if (loop) { stopLoop(); startLoop(); }
}

function setReviewTextSize(newSize = reviewTextSize) {
  reviewTextSize = newSize;
  const next = `review-text${reviewTextSize}`;
  if (lastReviewTextClass !== next) {
    const el = $w('#comment');
    if (lastReviewTextClass) el.customClassList.remove(lastReviewTextClass);
    el.customClassList.add(next);
    lastReviewTextClass = next;
  }
}
// In bindReviewItem, same idea per-item:


function setCommentMode(newCommentType = commentType){
    commentType = newCommentType;
    $w('#reviewsRepeater').forEachItem(($item, $itemData) => {
        $item(`#${commentType}Text`).text = $itemData.reviewText;
        $item('#comment').changeState(commentType);
    })
}

function setArrowColor(newColor = arrowColor){
    arrowColor = newColor;
    $w('#prevButton').style.iconColor = arrowColor ? arrowColor : "rgba(0,0,0,1)";
    $w('#nextButton').style.iconColor = arrowColor ? arrowColor : "rgba(0,0,0,1)";
}

async function updateReviewsList(newSort = sort, newFilters = filters){
    sort = newSort;
    if(newFilters) filters = newFilters;
    reviews = await mergeFilterSortReviews(allReviews, filters, sort, premium);
    updateDisplayedReviews();
}

// let existingStructuredData = [];

// // Retry mechanism to fetch existing structured data
// async function fetchExistingStructuredDataWithRetry(retries = 3, delay = 200) {
//     for (let i = 0; i < retries; i++) {
//         try {
//             const data = await wixSeoFrontend.structuredData;
//             if (Array.isArray(data) && data.length > 0) {
//                 return data;
//             }
//         } catch (err) {
//             console.error(`Attempt ${i + 1} to fetch structured data failed:`, err);
//         }
//         // Wait before retrying
//         await new Promise((res) => setTimeout(res, delay));
//     }
//     console.warn("No existing structured data found after retries.");
//     return [];
// }

// // Fetch existing structured data BEFORE onReady
// (async () => {
//     existingStructuredData = await fetchExistingStructuredDataWithRetry();
// })();

// /**
//  * Sets SEO structured data for reviews on widget load (using Organization schema)
//  * @param {Array} reviews - Array of review objects (with reviewerName, reviewText, rating, and reviewDate)
//  */
// export async function setReviewStructuredData(reviews) {
//     if (!Array.isArray(reviews) || reviews.length === 0) return;

//     const siteUrl = wixLocation.baseUrl;
//     const siteName = siteUrl.replace("https://", "").replace("www.", "");

//     const averageRating = calculateAverageRating(allReviews.flat());
//     const reviewCount = reviews.length;

//     const reviewSchema = {
//         "@context": "https://schema.org",
//         "@type": "Organization",
//         name: siteName,
//         url: siteUrl,
//         aggregateRating: {
//             "@type": "AggregateRating",
//             ratingValue: averageRating.toString(),
//             reviewCount: reviewCount.toString(),
//         },
//         review: reviews.map((review) => ({
//             "@type": "Review",
//             author: {
//                 "@type": "Person",
//                 name: review.reviewerName || "Anonymous",
//             },
//             reviewBody: review.reviewText,
//             reviewRating: {
//                 "@type": "Rating",
//                 ratingValue: review.rating?.toString() || "5",
//                 bestRating: "5",
//             },
//             datePublished: formatDate(review.reviewDate),
//         })),
//     };

//     //Merge the review schema with the existing schemas
//     const mergedData = [...existingStructuredData, reviewSchema];

//     // Apply merged schema
//     wixSeoFrontend.setStructuredData(mergedData);
// }

// /**
//  * Helper to format a date string into YYYY-MM-DD
//  * @param {string|Date} date
//  * @returns {string}
//  */
// function formatDate(date) {
//     try {
//         const d = new Date(date);
//         return d.toISOString().split("T")[0];
//     } catch {
//         return "2025-01-01"; // fallback
//     }
// }

// /**
//  * Calculates the average rating from an array of reviews
//  * @param {Array} reviews
//  */
// function calculateAverageRating(reviews) {
//     const total = reviews.reduce(
//         (sum, r) => sum + (parseFloat(r.rating) || 0),
//         0
//     );
//     return (total / reviews.length).toFixed(1);
// }

function arraysEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function changed(key, oldProps, newProps) {
  if (key === 'filters') return !arraysEqual(oldProps[key], newProps[key]);
  return oldProps[key] !== newProps[key];
}